/**
 * PIQ 8.5: Universal Insight Drawer
 * ==================================
 * Single deep dive surface for all insights.
 * Tabs: Summary, Insights, Evidence, Source
 */

import React, { useState, useEffect } from 'react';
import {
  X,
  Activity,
  Brain,
  Target,
  TrendingUp,
  Sparkles,
  Megaphone,
  Database,
  BookOpen,
  FileText,
  Link,
  ChevronRight,
  Info,
} from 'lucide-react';
import type { BrainContractSubset, InsightFeedItem, SourceType } from './operatorViewAdapter';

// ============================================================
// Types
// ============================================================

type DrawerTab = 'summary' | 'insights' | 'evidence' | 'source';

interface InsightDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  brainContract: BrainContractSubset;
  insights: {
    patterns: InsightFeedItem[];
    opportunities: InsightFeedItem[];
    marketing: InsightFeedItem[];
    dataHealth: InsightFeedItem[];
    learning: InsightFeedItem[];
  };
  /** Initial tab to open */
  initialTab?: DrawerTab;
  /** Filter to specific insight */
  filteredInsightId?: string;
  /** Filter to specific channel */
  filteredChannel?: string;
}

// ============================================================
// Source Badge
// ============================================================

const SOURCE_BADGE_CONFIG: Record<SourceType, { label: string; className: string }> = {
  piq: { label: 'PIQ', className: 'bg-violet-100 text-violet-700' },
  google: { label: 'Google', className: 'bg-blue-100 text-blue-700' },
  meta: { label: 'Meta', className: 'bg-sky-100 text-sky-700' },
  forecast: { label: 'Forecast', className: 'bg-gray-100 text-gray-700' },
  pattern: { label: 'Pattern', className: 'bg-indigo-100 text-indigo-700' },
  manual: { label: 'Manual', className: 'bg-amber-100 text-amber-700' },
  system: { label: 'System', className: 'bg-slate-100 text-slate-700' },
};

function SourceBadge({ sourceType }: { sourceType?: SourceType }) {
  if (!sourceType) return null;
  const config = SOURCE_BADGE_CONFIG[sourceType] || SOURCE_BADGE_CONFIG.piq;
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${config.className}`}>
      {config.label}
    </span>
  );
}

// ============================================================
// Tab Components
// ============================================================

function SummaryTab({ brainContract }: { brainContract: BrainContractSubset }) {
  const posture = brainContract.decisionPosture?.posture || 'monitor_only';
  const stateLabel = brainContract.businessState?.stateLabel || brainContract.businessState?.primaryState;
  const stateConfidence = brainContract.businessState?.confidence;
  const beliefLabel = brainContract.beliefSummary?.primary?.replace(/_/g, ' ');
  const beliefWeight = brainContract.beliefSummary?.weight;
  const phase = brainContract.retailPhase?.phase;
  const regimes = brainContract.regimeStatus?.activeRegimes || [];
  const cev = brainContract.computedExpectedValue;
  const rationale = brainContract.decisionPosture?.rationale || [];

  return (
    <div className="space-y-4">
      {/* Decision Rationale */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <Target className="w-4 h-4 text-violet-500" />
          Decision Rationale
        </h4>
        <div className="bg-gray-50 rounded-lg p-3">
          {rationale.length > 0 ? (
            <ul className="space-y-1 text-sm text-gray-700">
              {rationale.slice(0, 5).map((r, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <span className="text-gray-400">•</span>
                  {r}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500">No specific rationale recorded.</p>
          )}
        </div>
      </div>

      {/* Business Context */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <Activity className="w-4 h-4 text-violet-500" />
          Business Context
        </h4>
        <div className="grid grid-cols-2 gap-3">
          {/* State */}
          <div className="bg-violet-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Business State</p>
            <p className="text-sm font-medium text-violet-800">
              {stateLabel?.replace(/_/g, ' ') || 'Not determined'}
            </p>
            {stateConfidence && stateConfidence < 1 && (
              <p className="text-xs text-violet-600">{Math.round(stateConfidence * 100)}% confidence</p>
            )}
          </div>
          
          {/* Belief */}
          <div className="bg-indigo-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Active Belief</p>
            <p className="text-sm font-medium text-indigo-800">
              {beliefLabel || 'None active'}
            </p>
            {beliefWeight && (
              <p className="text-xs text-indigo-600">Weight: {beliefWeight.toFixed(2)}</p>
            )}
          </div>
          
          {/* Phase */}
          {phase && (
            <div className="bg-blue-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">Retail Phase</p>
              <p className="text-sm font-medium text-blue-800">
                {phase.replace(/_/g, ' ')}
              </p>
            </div>
          )}
          
          {/* CEV */}
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">CEV Status</p>
            <p className="text-sm font-medium text-gray-800">
              {cev?.metricsEvaluated || 0} metrics evaluated
            </p>
            {cev?.totalUnexpectedDelta !== undefined && (
              <p className="text-xs text-gray-600">
                Δ: ${Math.round(cev.totalUnexpectedDelta).toLocaleString()}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Active Regimes */}
      {regimes.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <Brain className="w-4 h-4 text-violet-500" />
            Active Regimes
          </h4>
          <div className="flex flex-wrap gap-2">
            {regimes.map((regime, idx) => (
              <span key={idx} className="px-2 py-1 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                {regime.name || regime}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function InsightsTab({ 
  insights, 
  filteredChannel,
  onItemClick,
}: { 
  insights: InsightDrawerProps['insights'];
  filteredChannel?: string;
  onItemClick?: (item: InsightFeedItem) => void;
}) {
  // Filter by channel if specified
  const filterByChannel = (items: InsightFeedItem[]) => {
    if (!filteredChannel) return items;
    return items.filter(i => 
      i.summary.toLowerCase().includes(filteredChannel.toLowerCase()) ||
      (i as any).retailer?.toLowerCase().includes(filteredChannel.toLowerCase())
    );
  };

  const groups = [
    { key: 'patterns', label: 'Patterns', icon: TrendingUp, items: filterByChannel(insights.patterns), color: 'blue' },
    { key: 'opportunities', label: 'Opportunities', icon: Sparkles, items: filterByChannel(insights.opportunities), color: 'emerald' },
    { key: 'marketing', label: 'Marketing', icon: Megaphone, items: filterByChannel(insights.marketing), color: 'blue' },
    { key: 'dataHealth', label: 'Data Health', icon: Database, items: filterByChannel(insights.dataHealth), color: 'gray' },
    { key: 'learning', label: 'Learning', icon: BookOpen, items: filterByChannel(insights.learning), color: 'violet' },
  ];

  return (
    <div className="space-y-4">
      {filteredChannel && (
        <div className="flex items-center gap-2 px-3 py-2 bg-violet-50 rounded-lg text-sm text-violet-700">
          <Info className="w-4 h-4" />
          Filtered to: <strong>{filteredChannel}</strong>
        </div>
      )}
      
      {groups.map((group) => (
        <div key={group.key}>
          <div className="flex items-center gap-2 mb-2">
            <group.icon className={`w-4 h-4 text-${group.color}-500`} />
            <h4 className="text-sm font-semibold text-gray-900">{group.label}</h4>
            <span className="text-xs text-gray-400">({group.items.length})</span>
          </div>
          
          {group.items.length > 0 ? (
            <div className="space-y-1.5">
              {group.items.slice(0, 5).map((item) => (
                <button
                  key={item.id}
                  onClick={() => onItemClick?.(item)}
                  className="w-full text-left p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors flex items-start gap-2"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-gray-800 truncate">{item.summary}</p>
                      <SourceBadge sourceType={item.sourceType} />
                    </div>
                    {item.impactBand && (
                      <p className="text-xs text-gray-500 mt-0.5">{item.impactBand}</p>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                </button>
              ))}
              {group.items.length > 5 && (
                <p className="text-xs text-gray-500 pl-2">+{group.items.length - 5} more</p>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic pl-6">No {group.label.toLowerCase()} this week</p>
          )}
        </div>
      ))}
    </div>
  );
}

function EvidenceTab({ brainContract }: { brainContract: BrainContractSubset }) {
  const dataQuality = brainContract.dataQuality;
  const conflicts = dataQuality?.conflicts || [];
  const missingBaselines = dataQuality?.missingBaselines || [];

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <Link className="w-4 h-4 text-violet-500" />
          Evidence Lineage
        </h4>
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-sm text-gray-600">
            All insights are derived from the Evidence Bundle, which aggregates:
          </p>
          <ul className="mt-2 space-y-1 text-sm text-gray-700">
            <li className="flex items-center gap-2">
              <span className="w-2 h-2 bg-violet-500 rounded-full" />
              SKU-level sales data (sku_detail_current)
            </li>
            <li className="flex items-center gap-2">
              <span className="w-2 h-2 bg-blue-500 rounded-full" />
              Forecast predictions (forecast_weekly)
            </li>
            <li className="flex items-center gap-2">
              <span className="w-2 h-2 bg-emerald-500 rounded-full" />
              DTC platform data (Shopify, Amazon, etc.)
            </li>
            <li className="flex items-center gap-2">
              <span className="w-2 h-2 bg-amber-500 rounded-full" />
              Google Ads recommendations (if connected)
            </li>
          </ul>
        </div>
      </div>

      {conflicts.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Data Conflicts</h4>
          <div className="space-y-1.5">
            {conflicts.slice(0, 5).map((conflict, idx) => (
              <div key={idx} className="p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                {typeof conflict === 'string' ? conflict : conflict.description || 'Conflict detected'}
              </div>
            ))}
          </div>
        </div>
      )}

      {missingBaselines.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Missing Baselines</h4>
          <div className="flex flex-wrap gap-1.5">
            {missingBaselines.map((baseline, idx) => (
              <span key={idx} className="px-2 py-1 bg-gray-100 rounded text-xs text-gray-600">
                {baseline}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SourceTab() {
  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <FileText className="w-4 h-4 text-violet-500" />
          Source Explanations
        </h4>
        
        <div className="space-y-3">
          <div className="bg-violet-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <SourceBadge sourceType="piq" />
              <span className="font-medium text-sm text-violet-800">PIQ Intelligence</span>
            </div>
            <p className="text-xs text-violet-700">
              Insights generated by the PIQ Brain through pattern matching, CEV analysis, 
              belief tracking, and cross-retailer causality detection.
            </p>
          </div>
          
          <div className="bg-blue-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <SourceBadge sourceType="google" />
              <span className="font-medium text-sm text-blue-800">Google Ads</span>
            </div>
            <p className="text-xs text-blue-700">
              Recommendations fetched from Google Ads API, including budget adjustments, 
              keyword suggestions, and campaign optimization opportunities.
            </p>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <SourceBadge sourceType="forecast" />
              <span className="font-medium text-sm text-gray-800">Forecast Model</span>
            </div>
            <p className="text-xs text-gray-700">
              Predictions from the LightGBM demand forecasting model, used to detect 
              unexpected demand variance and inventory planning signals.
            </p>
          </div>
          
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <SourceBadge sourceType="system" />
              <span className="font-medium text-sm text-slate-800">System</span>
            </div>
            <p className="text-xs text-slate-700">
              Data quality alerts, missing baseline warnings, and integrity checks 
              generated by the PIQ data pipeline.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Main Component
// ============================================================

export function InsightDrawer({
  isOpen,
  onClose,
  brainContract,
  insights,
  initialTab = 'summary',
  filteredInsightId,
  filteredChannel,
}: InsightDrawerProps) {
  const [activeTab, setActiveTab] = useState<DrawerTab>(initialTab);
  
  // Reset tab when opened
  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
    }
  }, [isOpen, initialTab]);

  const totalInsights = 
    insights.patterns.length + 
    insights.opportunities.length + 
    insights.marketing.length + 
    insights.dataHealth.length +
    insights.learning.length;

  if (!isOpen) return null;

  const tabs: { key: DrawerTab; label: string }[] = [
    { key: 'summary', label: 'Summary' },
    { key: 'insights', label: `Insights (${totalInsights})` },
    { key: 'evidence', label: 'Evidence' },
    { key: 'source', label: 'Source' },
  ];

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/30 z-40"
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-lg bg-white shadow-xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Intelligence Details</h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        
        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'text-violet-600 border-b-2 border-violet-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'summary' && <SummaryTab brainContract={brainContract} />}
          {activeTab === 'insights' && (
            <InsightsTab 
              insights={insights} 
              filteredChannel={filteredChannel}
            />
          )}
          {activeTab === 'evidence' && <EvidenceTab brainContract={brainContract} />}
          {activeTab === 'source' && <SourceTab />}
        </div>
      </div>
    </>
  );
}

export default InsightDrawer;
